import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ArrowLeft, FileText, Clock, CheckCircle2 } from "lucide-react";

interface AssessmentSelectorProps {
  moduleId: string;
  onSelectAssessment: (formId: string) => void;
  onClose: () => void;
}

export const AssessmentSelector = ({ moduleId, onSelectAssessment, onClose }: AssessmentSelectorProps) => {
  const { language } = useLanguage();
  const [assessmentForms, setAssessmentForms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAssessmentForms();
  }, [moduleId]);

  const fetchAssessmentForms = async () => {
    try {
      setLoading(true);
      
      const { data: forms, error } = await supabase
        .from('assessment_forms')
        .select('*')
        .eq('module_id', moduleId)
        .eq('is_active', true);

      if (error) throw error;
      setAssessmentForms(forms || []);
    } catch (error) {
      console.error('Error fetching assessment forms:', error);
      toast.error('Failed to load assessment forms');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">
            {language === 'en' ? 'Select Assessment' : 'Επιλέξτε Αξιολόγηση'}
          </h2>
          <p className="text-muted-foreground">
            {language === 'en' 
              ? 'Choose an assessment to test your knowledge'
              : 'Επιλέξτε μια αξιολόγηση για να δοκιμάσετε τις γνώσεις σας'
            }
          </p>
        </div>
        <Button variant="outline" onClick={onClose}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {language === 'en' ? 'Back' : 'Πίσω'}
        </Button>
      </div>

      {assessmentForms.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium mb-2">
              {language === 'en' ? 'No Assessments Available' : 'Δεν Υπάρχουν Διαθέσιμες Αξιολογήσεις'}
            </h3>
            <p className="text-muted-foreground">
              {language === 'en' 
                ? 'No assessment forms have been created for this module yet.'
                : 'Δεν έχουν δημιουργηθεί φόρμες αξιολόγησης για αυτή την ενότητα ακόμα.'
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {assessmentForms.map((form) => (
            <Card key={form.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>
                    {language === 'en' ? form.title_en : form.title_el}
                  </span>
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  {language === 'en' ? form.description_en : form.description_el}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <span className="flex items-center">
                      <FileText className="h-4 w-4 mr-1" />
                      {language === 'en' ? 'Quiz' : 'Κουίζ'}
                    </span>
                    <span className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      {language === 'en' ? 'Pass: ' : 'Επιτυχία: '}{form.pass_percentage}%
                    </span>
                  </div>
                  
                  <Button onClick={() => onSelectAssessment(form.id)}>
                    {language === 'en' ? 'Start Assessment' : 'Έναρξη Αξιολόγησης'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};